import React from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
  Heading,
  HStack,
  Icon,
  Input,
  InputField,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ScrollView,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { ArrowLeftRight, X } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { OralExamQuestion } from '@/common/api/models';
import type { ReferringResource } from '@/common/api/models/referringResource';
import type { ScheduledChangeSwapRequestReasonCode } from '@/common/api/models/scheduledChangeSwapRequestReasonCode';
import { questionTitleRepeatsTopic } from '../utils/questionTitleRepeatsTopic';
import { SwapAlternativeCards } from './SwapAlternativeCards';
import { SwapCurrentQuestion } from './SwapCurrentQuestion';
import { SwapReasonPicker } from './SwapReasonPicker';
import { SwapResourceRelevance } from './SwapResourceRelevance';

type SwapQuestionDialogProps = {
  isOpen: boolean;
  topicLabel: string;
  currentQuestion: OralExamQuestion;
  alternatives: OralExamQuestion[];
  referringResources?: ReferringResource[];
  selectedReplacementId?: string;
  selectedReason?: ScheduledChangeSwapRequestReasonCode;
  selectedResourceIds?: string[];
  note: string;
  errorMessage?: string;
  pending: boolean;
  onClose: () => void;
  onSelectReplacement: (questionId: string) => void;
  onSelectReason: (code: ScheduledChangeSwapRequestReasonCode) => void;
  onToggleResource: (resourceId: string, checked: boolean) => void;
  onNoteChange: (text: string) => void;
  onSubmit: () => void;
};

export function SwapQuestionDialog({
  isOpen,
  topicLabel,
  currentQuestion,
  alternatives,
  referringResources = [],
  selectedReplacementId,
  selectedReason,
  selectedResourceIds = [],
  note,
  errorMessage,
  pending,
  onClose,
  onSelectReplacement,
  onSelectReason,
  onToggleResource,
  onNoteChange,
  onSubmit,
}: SwapQuestionDialogProps) {
  const { t } = useTranslation();
  const handleClose = () => {
    if (pending) return;
    onClose();
  };
  const noteRequired = selectedReason === 'other';
  const canSubmit = !!selectedReplacementId
    && !!selectedReason
    && (!noteRequired || note.trim().length > 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={!pending}>
      <ModalBackdrop />
      <ModalContent
        maxWidth={640}
        softShadow="3"
        borderRadius="$xl"
        bg="$bgCard"
        testID="scheduled-swap-dialog"
      >
        <ModalHeader>
          <VStack space="xs" flex={1}>
            <Heading size="md">
              {t('lecturer.scheduledChanges.swap.title')}
            </Heading>
            <Text size="xs" color="$textHint">
              {topicLabel}
            </Text>
          </VStack>
          <ModalCloseButton
            p="$2"
            m="-$2"
            disabled={pending}
            onPress={handleClose}
            testID="scheduled-swap-close"
          >
            <Icon as={X} size="md" color="$textSubtle" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <ScrollView maxHeight={480}>
            <VStack space="md">
              <SwapCurrentQuestion
                question={currentQuestion.question ?? ''}
                fillKind={currentQuestion.fillKind}
                fills={currentQuestion.fills}
                hideTitle={questionTitleRepeatsTopic(currentQuestion.question, topicLabel)}
              />

              <VStack space="sm">
                <HStack justifyContent="space-between" alignItems="center">
                  <Text size="sm" fontFamily="$heading" color="$textHeading">
                    {t('lecturer.scheduledChanges.swap.pick')}
                  </Text>
                  <Text size="xs" color="$textHint">
                    {t('lecturer.scheduledChanges.swap.alternatives', { count: alternatives.length })}
                  </Text>
                </HStack>
                <SwapAlternativeCards
                  alternatives={alternatives}
                  topicLabel={topicLabel}
                  selectedReplacementId={selectedReplacementId}
                  onSelectReplacement={onSelectReplacement}
                />
              </VStack>

              <SwapReasonPicker
                selectedReason={selectedReason}
                onSelectReason={onSelectReason}
                showResourceReason={referringResources.length > 0}
              />
              {selectedReason === 'resource_not_exam_relevant' ? (
                <SwapResourceRelevance
                  resources={referringResources}
                  selectedIds={selectedResourceIds}
                  onToggle={onToggleResource}
                />
              ) : null}

              <Input size="sm">
                <InputField
                  placeholder={
                    noteRequired
                      ? t('lecturer.scheduledChanges.swap.noteRequired')
                      : t('lecturer.scheduledChanges.swap.notePlaceholder')
                  }
                  value={note}
                  onChangeText={onNoteChange}
                  testID="scheduled-swap-note"
                />
              </Input>
            </VStack>
          </ScrollView>
        </ModalBody>
        {!!errorMessage && (
          <Box px="$4" pb="$2">
            <Text size="sm" color="$statusError" testID="scheduled-swap-error">
              {errorMessage}
            </Text>
          </Box>
        )}
        <ModalFooter>
          <HStack space="sm" justifyContent="flex-end" width="$full">
            <Button
              variant="outline"
              action="secondary"
              onPress={handleClose}
              isDisabled={pending}
              testID="scheduled-swap-cancel"
            >
              <ButtonText>{t('lecturer.scheduledChanges.swap.cancel')}</ButtonText>
            </Button>
            <Button
              onPress={onSubmit}
              isDisabled={!canSubmit || pending}
              accessibilityState={{ disabled: !canSubmit || pending }}
              testID="scheduled-swap-submit"
            >
              {pending ? <ButtonSpinner mr="$2" /> : null}
              <ButtonIcon as={ArrowLeftRight} mr="$2" />
              <ButtonText>{t('lecturer.scheduledChanges.swap.submit')}</ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
